import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';

/**
 * Backs the `aria-describedby` wiring. Several of these can be mounted at once — two
 * carts over the same attribute, or several attributes in one cart — so the id has to
 * be unique per instance.
 */
let instanceCount = 0;

/**
 * A checkbox bound to one cart attribute.
 *
 * Toggling POSTs to /cart/update.js: `'true'` when checked, an empty string to clear
 * it, since `'false'` would persist as a value the fulfilment side would have to know
 * to ignore. A failed write is rolled back, so the box never claims a setting the cart
 * doesn't have.
 *
 * Renders its own markup, so a host only places the element and everything it needs
 * arrives as an attribute — or, failing that, from window.SBCartExtras (published by
 * snippets/custom-cart-extras). That is what lets a Rebuy Smart Cart template, which
 * lives in the Rebuy dashboard and can't see Liquid, emit
 * `<cart-attribute-checkbox extra="blindShipping">` and still pick up the label and
 * description a merchant set in theme settings.
 *
 * @typedef {{ enabled?: boolean, attribute?: string, label?: string, description?: string }} CartExtra
 *
 * Attributes:
 *   - `extra`          — key in window.SBCartExtras to fall back to, e.g. "blindShipping"
 *   - `attribute-name` — cart attribute to write. Falls back to the extra's `attribute`
 *   - `label`          — checkbox label. Falls back to the extra's `label`
 *   - `description`    — hint under the label. Falls back to the extra's `description`
 *   - `checked`        — `"true"` checks the box, anything else clears it. Absent leaves
 *                        the rendered state alone. Read on connect and on every rebuild
 *   - `form`           — id of the form the input submits with. The input only gets a
 *                        `name` when this is set, so an element mounted elsewhere can't
 *                        ride along with a form it doesn't own
 *
 * @extends Component
 */
export class CartAttributeCheckbox extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  #descriptionId = `cart-attribute-checkbox-description-${++instanceCount}`;

  /**
   * Puts the markup back whenever something strips it.
   *
   * Load-bearing, not belt-and-braces. The theme cart renders this element empty, so a
   * cart re-render morphs an empty subtree over our live one and deletes the checkbox
   * (morph.js updateChildren removes old children with no new counterpart). The
   * documented hook for this, `updatedCallback`, never fires: morph hands
   * MORPH_OPTIONS.onAfterUpdate the node from the *incoming* HTML, that HTML is parsed
   * with DOMParser, and a DOMParser document has no custom element registry — so the
   * node is never upgraded and `instanceof Component` is false. A MutationObserver is
   * told by the DOM instead of by the framework, so it also covers a host framework
   * re-rendering over our children.
   */
  #childListObserver = new MutationObserver(() => this.#render());

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    // Delegated from the host, not bound to the input: the input is rebuilt whenever
    // something strips it, and a listener on it would go with it — leaving a checkbox
    // that toggles nothing.
    this.addEventListener('change', this.#handleChange);
    this.#childListObserver.observe(this, { childList: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#childListObserver.disconnect();
    this.#activeFetch?.abort();
  }

  get attributeName() {
    return this.getAttribute('attribute-name') || this.#extra()?.attribute || '';
  }

  get label() {
    return this.getAttribute('label') || this.#extra()?.label || '';
  }

  get description() {
    return this.getAttribute('description') || this.#extra()?.description || '';
  }

  /** @returns {HTMLInputElement | null} */
  get checkbox() {
    const input = this.querySelector('input[type="checkbox"]');

    return input instanceof HTMLInputElement ? input : null;
  }

  /**
   * This element's entry in window.SBCartExtras, when it named one.
   *
   * Read fresh rather than cached: the global is written by an inline script in the
   * document head, and reading it lazily means load order can't matter.
   *
   * @returns {CartExtra | null}
   */
  #extra() {
    const key = this.getAttribute('extra');
    if (!key) return null;

    const extras = /** @type {Record<string, CartExtra> | undefined} */ (Reflect.get(window, 'SBCartExtras'));

    return extras?.[key] ?? null;
  }

  /**
   * Builds the checkbox and its hint. No-op once an input is present, including one a
   * host rendered itself — which is why the change event is delegated rather than
   * bound here.
   */
  #render() {
    if (this.checkbox) return;

    const label = this.label;
    if (!label) {
      console.error('[cart-attribute-checkbox] no label, from an attribute or SBCartExtras', this);
      return;
    }

    const labelElement = document.createElement('label');
    labelElement.className = 'cart-attribute-checkbox__label cart-primary-typography';

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.className = 'cart-attribute-checkbox__checkbox';
    input.value = 'true';

    // Only a host that named a form wants this in a form submission; without that, a
    // name would quietly attach the value to whatever form happens to enclose it.
    const form = this.getAttribute('form');
    if (form) {
      input.setAttribute('form', form);
      input.name = `attributes[${this.attributeName}]`;
    }

    const text = document.createElement('span');
    text.textContent = label;
    labelElement.append(input, text);

    const description = this.description;
    let descriptionElement = null;

    if (description) {
      input.setAttribute('aria-describedby', this.#descriptionId);

      descriptionElement = document.createElement('p');
      descriptionElement.className = 'cart-attribute-checkbox__description cart-primary-typography';
      descriptionElement.id = this.#descriptionId;
      descriptionElement.textContent = description;
    }

    // Set before the input is in the document, so a rebuild mid-morph can't paint an
    // unchecked box for a customer who has the attribute set.
    input.checked = this.getAttribute('checked') === 'true';

    this.append(labelElement);
    if (descriptionElement) this.append(descriptionElement);
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    if (event.target !== this.checkbox) return;

    this.#update(/** @type {HTMLInputElement} */ (event.target).checked);
  };

  /**
   * Writes the attribute, leaving the checkbox where the customer put it.
   *
   * Optimistic rather than busy-disabled: a checkbox that flickers to disabled on
   * every click reads as broken, and there is nothing to guard — the write is
   * cart-attribute only, so it can't invalidate a price or a line key.
   *
   * @param {boolean} checked
   */
  async #update(checked) {
    if (!this.attributeName) {
      console.error('[cart-attribute-checkbox] no attribute-name, nothing to write', this);
      return;
    }

    // Last toggle wins. Aborting doesn't unmake a write the server already accepted,
    // but each request carries the absolute state rather than a delta, so the last one
    // sent is the one that decides.
    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ attributes: { [this.attributeName]: checked ? 'true' : '' } }),
      });

      const response = await fetch(Theme.routes.cart_update_url, { ...config, signal: abortController.signal });
      if (!response.ok) throw new Error(`Cart update failed with ${response.status}`);

      // A newer toggle may have landed while this was in flight; it owns the checkbox
      // now, and its own request will settle it.
      if (this.#activeFetch !== abortController) return;

      this.#sync(checked);
    } catch (error) {
      if (abortController.signal.aborted) return;

      console.error('[cart-attribute-checkbox] update failed', error);

      if (this.#activeFetch === abortController && this.checkbox) this.checkbox.checked = !checked;
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  }

  /**
   * Puts every checkbox over the same attribute on the confirmed state, and mirrors it
   * onto their `checked` attribute so the next rebuild agrees.
   *
   * The theme drawer and a Rebuy flyout can both be mounted at once over one cart
   * attribute, and only the one that was clicked hears the event. Matching on the
   * attribute name matters because this is generic — two checkboxes in the same cart
   * are normally writing different attributes and must not follow each other.
   *
   * The clicked one is included on purpose: an unrelated cart re-render can land
   * mid-write and morph it back to the state the server held before this toggle.
   *
   * @param {boolean} checked
   */
  #sync(checked) {
    for (const element of document.querySelectorAll('cart-attribute-checkbox')) {
      if (!(element instanceof CartAttributeCheckbox)) continue;
      if (element.attributeName !== this.attributeName) continue;

      element.setAttribute('checked', String(checked));

      const checkbox = element.checkbox;
      if (checkbox) checkbox.checked = checked;
    }
  }
}

if (!customElements.get('cart-attribute-checkbox')) {
  customElements.define('cart-attribute-checkbox', CartAttributeCheckbox);
}
